'use client';

import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  listCases,
  listCaseAccess,
  listTenantUsers,
  getTenantCaseAccessSetting,
  type CaseSummary,
  type CaseAccessUser,
  type TenantUser,
} from '@/shared/csm-cases-client';
import { useReactiveAuthTokens } from '@/app/contexts';
import { getTenantIdFromToken } from '@/shared/csm-utils';

/**
 * TanStack Query hooks for the CSM case-management dashboard — same pattern as
 * the rest of the app (query-key factory + ``useQuery``; mutations are plain
 * client calls followed by ``queryClient.invalidateQueries`` at the call site).
 *
 * Every key is scoped by the active tenant id. The ``/api/protected/csm/*``
 * proxy routes resolve the tenant from the session's current tenant, so a CSM
 * switching tenant context (without a full remount) would otherwise read the
 * previous tenant's cached cases / access / user list until ``staleTime``.
 */
export const caseManagementKeys = {
  all: ['csm', 'cases'] as const,
  cases: (tenantId: string | null) => [...caseManagementKeys.all, 'list', tenantId] as const,
  access: (tenantId: string | null, caseId: string) =>
    [...caseManagementKeys.all, 'access', tenantId, caseId] as const,
  users: (tenantId: string | null) => [...caseManagementKeys.all, 'users', tenantId] as const,
  accessSetting: (tenantId: string | null) =>
    [...caseManagementKeys.all, 'access-setting', tenantId] as const,
};

/**
 * Active tenant id from the ID token — mirrors the server-side
 * ``resolveUserContext`` so client cache keys match what the proxy routes
 * actually query (it follows a CSM cell-switch / tenant override).
 */
export function useActiveTenantId(): string | null {
  const { idToken } = useReactiveAuthTokens();
  return useMemo(() => getTenantIdFromToken(idToken), [idToken]);
}

export function useCsmCases(): UseQueryResult<CaseSummary[], Error> {
  const tenantId = useActiveTenantId();
  return useQuery<CaseSummary[], Error>({
    queryKey: caseManagementKeys.cases(tenantId),
    queryFn: ({ signal }) => listCases(signal),
    // The /api/protected/csm/* proxy requires a tenant (resolveUserContext).
    // Stay disabled until tenant context resolves so a CSM doesn't get a failed
    // request / error UI cached against the tenantId: null key before switch.
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

export function useCaseAccess(caseId: string | null): UseQueryResult<CaseAccessUser[], Error> {
  const tenantId = useActiveTenantId();
  return useQuery<CaseAccessUser[], Error>({
    queryKey: caseManagementKeys.access(tenantId, caseId ?? ''),
    queryFn: ({ signal }) => listCaseAccess(caseId as string, signal),
    enabled: Boolean(caseId) && Boolean(tenantId),
  });
}

export function useTenantUsers(enabled: boolean): UseQueryResult<TenantUser[], Error> {
  const tenantId = useActiveTenantId();
  return useQuery<TenantUser[], Error>({
    queryKey: caseManagementKeys.users(tenantId),
    queryFn: ({ signal }) => listTenantUsers(signal),
    enabled: enabled && Boolean(tenantId),
    staleTime: 5 * 60_000,
  });
}

/**
 * The tenant-wide "everyone sees every case" master switch. When true, per-case
 * access management is bypassed (and the dashboard disables its per-case
 * controls); when false, access is managed per case.
 */
export function useTenantCaseAccessSetting(): UseQueryResult<boolean, Error> {
  const tenantId = useActiveTenantId();
  return useQuery<boolean, Error>({
    queryKey: caseManagementKeys.accessSetting(tenantId),
    queryFn: ({ signal }) => getTenantCaseAccessSetting(signal),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}
